import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ApiEnvelope,
  ChapterProgressResponse,
  DashboardSummary,
  ProgressEventPayload,
  ResumeResponse,
  StreakResponse,
  UserProgress,
} from "@/lib/api-types";

/**
 * GET /progress/me/dashboard -> { last_7_days, recent_chapters }
 */
export function useDashboard() {
  return useQuery({
    queryKey: qk.progress.dashboard(),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<DashboardSummary>>("/progress/me/dashboard");
      return (
        unwrap(res.data) ?? {
          last_7_days: {
            total_seconds_studied: 0,
            total_chat_messages: 0,
            total_quiz_attempts: 0,
            daily_seconds: [],
          },
          recent_chapters: [],
        }
      );
    },
    staleTime: 30 * 1000,
  });
}

/**
 * GET /progress/me/resume -> { resume: UserProgress | null }
 */
export function useResume() {
  return useQuery({
    queryKey: qk.progress.resume(),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ResumeResponse>>("/progress/me/resume");
      return unwrap(res.data)?.resume ?? null;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * GET /progress/me/streak -> { current_streak_days, longest_streak_days_60d, active_days_60d }
 */
export function useStreak() {
  return useQuery({
    queryKey: qk.progress.streak(),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<StreakResponse>>("/progress/me/streak");
      return (
        unwrap(res.data) ?? {
          current_streak_days: 0,
          longest_streak_days_60d: 0,
          active_days_60d: 0,
        }
      );
    },
    staleTime: 60 * 1000,
  });
}

/**
 * GET /progress/me/chapter/{chapter_id} -> { progress, quiz_attempts }
 */
export function useChapterProgress(chapterId: string | undefined) {
  return useQuery({
    queryKey: qk.progress.chapter(chapterId ?? ""),
    enabled: !!chapterId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChapterProgressResponse>>(
        `/progress/me/chapter/${encodeURIComponent(chapterId!)}`,
      );
      return unwrap(res.data) ?? { progress: null, quiz_attempts: [] };
    },
    staleTime: 15 * 1000,
  });
}

/**
 * POST /progress/event -> { progress: UserProgress }
 * Fire-and-forget by default. Returned promise resolves to the new
 * rollup so the dashboard / chapter progress queries can be invalidated.
 */
export function useRecordProgressEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ProgressEventPayload) => {
      const res = await api.post<ApiEnvelope<{ progress: UserProgress }>>("/progress/event", payload);
      return unwrap(res.data);
    },
    onSuccess: (_data, payload) => {
      // Soft-invalidate the queries the new rollup affects. Don't refetch
      // immediately -- they'll refresh next time the user navigates.
      qc.invalidateQueries({ queryKey: qk.progress.dashboard() });
      qc.invalidateQueries({ queryKey: qk.progress.resume() });
      qc.invalidateQueries({ queryKey: qk.progress.chapter(payload.chapter_id) });
    },
  });
}
